import React from 'react';
import BoldText from '@/components/BoldText';
import MediumText from '@/components/MediumText';
import { ModalConditionSummaryStyled } from '@/components/ModalConditionSummary/styled';
import { openCrisp } from '@/helpers';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { IResponseApi } from '@/types/apis/response';
import { RelationCondition } from '@/types/newSetup.type';
import { Divider, Link, List } from '@shopify/polaris';
import { EditIcon } from '@shopify/polaris-icons';
import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import BaseModal from '../BaseModal';
import BoldSmallText from '../BoldSmallText';
import { MetafieldNumericValuePrefix, MetafieldType } from '@/constants/enum';

interface IProps {
  id: number;
  onEdit: () => void;
  isOpenModal: boolean;
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IAppliedProps {
  products?: IResponseApi.IRuleCondition['products'];
  collections?: IResponseApi.IRuleCondition['collections'];
  vendors?: IResponseApi.IRuleCondition['vendors'];
  countries?: IResponseApi.IRuleCondition['countries'];
  zipcodes?: IResponseApi.IRuleCondition['zipcodes'];
  variantMetafields?: IResponseApi.IRuleCondition['variantMetafields'];
  productMetafields?: IResponseApi.IRuleCondition['productMetafields'];
  isException?: boolean;
  subtitle?: string | React.ReactNode;
  SKUs?: IResponseApi.IRuleCondition['SKUs'];
  inventoryLocations?: IResponseApi.IRuleCondition['inventoryLocations'];
  inventoryQuantities?: IResponseApi.IRuleCondition['inventoryQuantities'];
  tags?: IResponseApi.IRuleCondition['tags'];
  typeRelationOfItemCondition?: IResponseApi.IRuleCondition['typeRelationOfItemCondition'];
  hideRelationType?: boolean;
}

interface ISpecificDataProps {
  products?: {
    name: string;
    variants: string[];
  }[];
  productMetafields?: {
    name: string;
    value: string[];
  }[];
  variantMetafields?: {
    name: string;
    value: string[];
  }[];
  countries?: {
    name: string;
    states: string[];
  }[];
  inventoryLocations?: {
    name: string;
  }[];
  zipcodes?: string[];
}

const getItemValues = (item: any): any[] => {
  if (item.type && [MetafieldType.Decimal, MetafieldType.Integer].includes(item.type)) {
    const minValue = item?.value?.find((v: any) => typeof v === 'string' && v.startsWith(MetafieldNumericValuePrefix.Min));
    const maxValue = item?.value?.find((v: any) => typeof v === 'string' && v.startsWith(MetafieldNumericValuePrefix.Max));
    return ['Minimum: ' + (minValue || 'none'), 'Maximum' + maxValue || 'none'];
  }
  if (item?.variants) return item.variants;
  if (item?.states) return item.states;
  if (typeof item?.value === 'string') return item.value.split(',');
  if (Array.isArray(item?.value)) return item.value;
  return [];
};

const AppliedContent = ({
  products,
  collections,
  vendors,
  countries,
  zipcodes,
  SKUs,
  tags,
  inventoryLocations,
  inventoryQuantities,
  productMetafields,
  variantMetafields,
  isException,
  subtitle,
  typeRelationOfItemCondition,
  hideRelationType,
}: IAppliedProps) => {
  const title = useMemo(() => {
    if (products) return 'Products, variants';
    if (collections) return 'Collections';
    if (vendors) return 'Vendors';
    if (SKUs) return 'SKUs';
    if (inventoryLocations) return 'Inventory locations';
    if (tags) return 'Product tags';
    if (inventoryQuantities) return 'Inventory quantity';
    if (productMetafields) return 'Product metafields';
    if (variantMetafields) return 'Variant metafields';
    return '';
  }, [products, collections, vendors, SKUs, inventoryLocations, tags, inventoryQuantities, productMetafields, variantMetafields]);

  const renderSpecificData = (
    products: ISpecificDataProps['products'],
    countries: ISpecificDataProps['countries'],
    inventoryLocations: ISpecificDataProps['inventoryLocations'],
    zipcodes: ISpecificDataProps['zipcodes'],
    productMetafields: ISpecificDataProps['productMetafields'],
    variantMetafields: ISpecificDataProps['variantMetafields'],
  ) => {
    const stringArrayData = collections || vendors || SKUs || tags || inventoryQuantities;
    const specificData = products || countries || inventoryLocations || productMetafields || variantMetafields;
    return (
      <>
        {specificData?.map((item: any, index: number) => (
          <List.Item key={index}>
            <div className="d-flex flex-column">
              <p className="mb-4">{item.name}</p>
              <List type="bullet">
                {getItemValues(item)?.map((el: any, index: number) => (
                  <List.Item key={index}>{el}</List.Item>
                ))}
              </List>
            </div>
          </List.Item>
        ))}
        {stringArrayData?.map((item: any, index: number) => (
          <List.Item key={index}>{item}</List.Item>
        ))}
        {zipcodes?.length ? <div className="mb-8">{zipcodes[0].split(',').join(', ')}</div> : null}
      </>
    );
  };

  return (
    <>
      <div className={`applied-item-title ${isException ? 'text--critical' : ''} no-width`}>
        <BoldSmallText>
          {isException || hideRelationType ? '' : typeRelationOfItemCondition?.toUpperCase()} {isException ? 'Exceptions' : title}{' '}
          {isException || inventoryQuantities ? '' : subtitle}
        </BoldSmallText>
      </div>
      <div className={`applied-item-info no-padding-left ${zipcodes?.length || inventoryQuantities?.length ? '' : 'pl-16'}`}>
        {inventoryQuantities ? (
          inventoryQuantities[0]
        ) : (
          <List type="number">
            {renderSpecificData(products, countries, inventoryLocations, zipcodes, productMetafields, variantMetafields)}
          </List>
        )}
      </div>
    </>
  );
};

const renderAppliedContent = (
  typeRelationOfItemCondition: RelationCondition,
  subtitle: React.ReactNode,
  items: any[],
  key: string,
  isException?: boolean,
  hideRelationType?: boolean,
  hideDivider?: boolean,
) => {
  return items.length ? (
    <>
      <AppliedContent
        typeRelationOfItemCondition={typeRelationOfItemCondition}
        subtitle={subtitle}
        {...{ [key]: items }}
        isException={isException}
        hideRelationType={hideRelationType}
      />
      {!hideDivider ? <Divider /> : null}
    </>
  ) : null;
};

const ModalConditionSummary = ({ isOpenModal, setIsOpenModal, ...props }: IProps) => {
  const isSkip = useSelector(isSkipApiSelector);
  const {
    data,
    isFetching: isLoadingGetRuleConditionDetail,
    isError,
  } = apiCaller.useGetRuleConditionDetailQuery(props.id, {
    skip: !props.id || isSkip,
  });
  const description = useMemo(() => {
    switch (data?.data?.type) {
      case 'applied':
        return <BoldSmallText>include</BoldSmallText>;
      case 'excluded':
        return (
          <>
            <BoldSmallText>exclude</BoldSmallText>
          </>
        );
      default:
        return <></>;
    }
  }, [data?.data?.type]);

  const isShowAllProduct = useMemo(() => {
    return data?.data?.type === 'all' && !data?.data?.countries?.length && !data?.data?.zipcodes?.length;
  }, [data?.data]);

  const contents = useMemo(() => {
    const excludeKeys = ['countries', 'zipcodes', 'type', 'typeRelationOfItemCondition', 'exceptions', 'name'];
    const dataContents = Object.entries(data?.data || {})
      .map(([key, value]) => ({ key, value }))
      .filter((item) => item.value?.length && !excludeKeys?.includes(item.key));
    return dataContents;
  }, [data?.data]);

  const locationConditionLength = useMemo(() => {
    let count = 0;
    if (data?.data?.zipcodes?.length || data?.data?.countries?.length) count += 1;
    return count;
  }, [data?.data]);

  return (
    <>
      <BaseModal
        title={<div className="text-ellipsis">Display condition of {data?.data?.name || ''}</div>}
        open={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
        }}
        primaryAction={{
          content: 'Cancel',
          onAction: () => {
            setIsOpenModal(false);
          },
        }}
        secondaryActions={[{ content: 'Edit', icon: EditIcon, onAction: props.onEdit }]}
        description={
          isError ? (
            ''
          ) : isShowAllProduct ? (
            <>
              This rule shows on <BoldSmallText>All Products</BoldSmallText>
            </>
          ) : (
            'This rule will show when all following conditions match'
          )
        }
        loading={isLoadingGetRuleConditionDetail}
        hideChildren={isShowAllProduct}
      >
        {isError ? (
          <MediumText>
            Conditions are not synced.{' '}
            <Link monochrome onClick={openCrisp}>
              Contact us
            </Link>
          </MediumText>
        ) : (
          <ModalConditionSummaryStyled>
            <>
              <div className="mb-4">
                <BoldText className="bold-text-styled">Item conditions</BoldText>
                {data?.data?.type === 'all' && (
                  <div className="mt-8">
                    This rule shows on <BoldSmallText>All Products</BoldSmallText>
                    <Divider />
                  </div>
                )}
              </div>
              <div className="mt-16">
                {contents.map((item, index) => (
                  <React.Fragment key={item.key}>
                    {renderAppliedContent(
                      data?.data?.typeRelationOfItemCondition || RelationCondition.OR,
                      description,
                      item.value,
                      item.key,
                      false,
                      contents?.length === 1 || index === 0,
                      contents?.length === 1 && !locationConditionLength,
                    )}
                  </React.Fragment>
                ))}

                {data?.data?.exceptions?.length ? (
                  <>
                    <div className="mt-8">
                      {renderAppliedContent(
                        data?.data?.typeRelationOfItemCondition || RelationCondition.OR,
                        description,
                        data?.data?.exceptions,
                        'products',
                        true,
                        true,
                        !locationConditionLength,
                      )}
                    </div>
                  </>
                ) : null}
              </div>

              {data?.data?.countries?.length ? (
                <div className="mt-16">
                  <div className="mb-4 mt-8">
                    <BoldText className="bold-text-styled">Countries, Regions</BoldText>
                  </div>
                  <div className="mb-8">
                    {renderAppliedContent(
                      data?.data?.typeRelationOfItemCondition || RelationCondition.OR,
                      '',
                      data?.data?.countries,
                      'countries',
                      false,
                      true,
                      true,
                    )}
                  </div>
                </div>
              ) : null}

              {data?.data?.zipcodes?.length ? (
                <>
                  <div className="mb-4 mt-8">
                    <BoldText className="bold-text-styled">Zipcode</BoldText>
                  </div>
                  {renderAppliedContent(
                    data?.data?.typeRelationOfItemCondition || RelationCondition.OR,
                    '',
                    data?.data?.zipcodes,
                    'zipcodes',
                    false,
                    true,
                    true,
                  )}
                </>
              ) : null}
            </>
          </ModalConditionSummaryStyled>
        )}
      </BaseModal>
    </>
  );
};

export default memo(ModalConditionSummary);
